import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import toast from 'react-hot-toast'

import { useStorage, useUser } from '../context'
import { createUser } from '../firebase/utils'
import { AnyProfile, NewUser, ProfileRole } from '../types'
import { buildProfileSections } from '../utilFunctions/buildProfileSection'

const deepEqual = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b)

export interface Credentials {
  currentPassword: string
  password: string
  confirmPassword: string
  email: string
  confirmEmail: string
  currentEmail: string
}

const INITIAL_CREDENTIALS = {
  currentPassword: '',
  password: '',
  confirmPassword: '',
  email: '',
  confirmEmail: '',
  currentEmail: '',
}

export const useProfileFormController = <T extends AnyProfile>({
  initial,
  isNewUser,
  // onSave,
}: {
  initial: T
  isNewUser: boolean
  // onSave: (values: T) => Promise<void>
}) => {
  const { uploadImage } = useStorage()
  const { updateUser, changeEmail, changePassword, setOnEditionMode } =
    useUser()
  const initialRef = useRef<T>(initial)
  const [values, setValues] = useState<T>(initial)
  const [credentials, setCredentials] =
    useState<Credentials>(INITIAL_CREDENTIALS)
  const [loading, setLoading] = useState(false)

  const isChangingPassword =
    credentials.password !== '' && credentials.confirmPassword !== ''
  const isChangingEmail =
    credentials.email !== '' && credentials.confirmEmail !== ''

  const updateState = useCallback(
    // (key: string, value: number | string | string[] | null)
    <K extends keyof T>(key: K, value: T[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const updateInput = useCallback(
    (
      e: ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      const { name, value } = e.target

      setValues((prev) => ({ ...prev, [name]: value }))
    },
    [],
  )

  const handleCoverUpload = useCallback(
    (refId: string, blob: Blob) => {
      const profileImageRefId = refId === 'image' ? values.uid || refId : refId
      const imageURL = uploadImage(profileImageRefId, blob)
      return imageURL
    },
    [uploadImage, values.uid],
  )

  const handleCredentials = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (isNewUser) {
      setValues((prev) => ({ ...prev, [name]: value }))
    } else {
      setCredentials((prev) => ({ ...prev, [name]: value }))
    }
  }, [])

  const saveCredentials = useCallback(
    async (setShowModal: Dispatch<SetStateAction<boolean>>) => {
      const { email, confirmPassword, currentPassword } = credentials
      setLoading(true)
      try {
        if (isChangingEmail) {
          await changeEmail(email, values.role)
          const nextValues = { ...values, email }
          setValues(nextValues)
          initialRef.current = nextValues
        }

        if (isChangingPassword) {
          await changePassword(currentPassword, confirmPassword)
        }
        setCredentials(INITIAL_CREDENTIALS)
        // setShowCredentials(false)
        toast.success('Se han actualizado las credenciales')
      } catch (error) {
        console.log(error)
        toast.error('Se ha producido un error')
      } finally {
        setLoading(false)
        setShowModal(false)
      }
    },
    [
      changeEmail,
      changePassword,
      credentials,
      isChangingEmail,
      isChangingPassword,
      values,
    ],
  )

  const reset = useCallback(() => setValues(initialRef.current), [])

  const isDirty = useMemo(() => {
    if (isNewUser) {
      return (
        values.name.length !== 0 &&
        values.lastname.length !== 0 &&
        values.phone.length !== 0 &&
        values.birthDate.length !== 0 &&
        (values as NewUser).password.length !== 0 &&
        (values as NewUser).password === (values as NewUser).confirmPassword &&
        values.email.length !== 0 &&
        (values as NewUser).email === (values as NewUser).confirmEmail &&
        !deepEqual(values, initialRef.current)
      )
    } else {
      return !deepEqual(values, initialRef.current)
    }
  }, [values])

  const sections = useMemo(
    () => buildProfileSections(values.role as ProfileRole),
    [values.role, isNewUser],
  )

  const save = useCallback(async () => {
    setLoading(true)
    try {
      // await onSave(values)
      await updateUser(values)
      initialRef.current = values // commit
      toast.success('Se han guardado los cambios')
    } catch (error) {
      console.log(error)
      toast.error('Se ha producido un error')
    } finally {
      setLoading(false)
      setOnEditionMode(false)
    }
  }, [values])

  const create = useCallback(async () => {
    setLoading(true)
    if (
      (values as NewUser).password !== (values as NewUser).confirmPassword ||
      values.email === '' ||
      !values.role
    ) {
      setLoading(false)
      return null
    }

    try {
      const createResult = await createUser(values, values.role, reset)
      if (typeof createResult === 'string') throw new Error(createResult)
      toast.success('El usuario se ha creado con exito')
      return createResult
    } catch (error) {
      console.log(error)
      toast.error('Se ha producido un error')
      return null
    } finally {
      setLoading(false)
      setOnEditionMode(false)
    }
  }, [values])

  const cancel = useCallback(() => {
    reset()
    setOnEditionMode(false)
  }, [values])

  return {
    values,
    initialValues: initialRef.current,
    formActions: {
      updateState,
      handleCoverUpload,
      updateInput,
      handleCredentials,
    },
    credentials,
    setValues,
    reset,
    saveCredentials,
    save,
    create,
    cancel,
    isDirty,
    loading,
    sections,
    isNewUser,
  }
}
